import { useMemo } from 'react';
import { useKFlags } from '@/hooks/use-k-flags';
import { jobsEmploymentsOptions } from '@/constants/job-constants';
import { useWorkTypes } from './workTypesApi';

export interface WorkTypeOption {
  label: string;
  value: string | number;
  /**
   * Set on a retired type that is only in the list so an already-saved value
   * still renders. Selects that honour it (react-select) grey the row out and
   * refuse the click; the value it belongs to still displays normally.
   */
  isDisabled?: boolean;
}

export interface UseWorkTypeOptionsArgs {
  /**
   * Include disabled work types, selectable. Filters want them, pickers do not
   * (CORE-5331).
   *
   * A filter has to offer every type an existing record might carry: turning a
   * type off hides it from new work, it does not rewrite the jobs already on
   * it, and without this those records cannot be filtered for at all.
   *
   * A picker is the opposite — offering a disabled type there would let someone
   * assign retired work, which is what "disabled" exists to prevent. Leave this
   * off for anything that writes a value, and reach for `ensureIds` instead.
   */
  includeDisabled?: boolean;
  /**
   * Ids to keep in the list even when the type is disabled, marked
   * `isDisabled` so they display but cannot be picked.
   *
   * Pass the value the record is already saved as. A picker built from the
   * enabled types alone cannot render a value whose type was retired after the
   * record was saved: the select finds no matching option and falls back to its
   * placeholder, so a job saved as "Seasonal" reads as empty. On a required
   * field that also blocks the form until someone rewrites the work type — an
   * unrelated edit silently changing saved data.
   *
   * Ignored when `includeDisabled` is set, which already keeps everything.
   */
  ensureIds?: Array<string | number | null | undefined>;
}

/**
 * Source for the job/requisition "work type" dropdown.
 * Flag OFF → legacy static employment-type options (string enum values).
 * Flag ON  → custom work types (numeric ids as values); enabled only, unless
 *            `includeDisabled` or `ensureIds` widens it.
 *
 * Consumers branch their save payload on `isWorkTypesEnabled`:
 * ON → send `ats_work_type_id` (the chosen value), OFF → keep `employment_type`.
 */
export const useWorkTypeOptions = ({
  includeDisabled = false,
  ensureIds = [],
}: UseWorkTypeOptionsArgs = {}): {
  options: WorkTypeOption[];
  isWorkTypesEnabled: boolean;
  isLoading: boolean;
} => {
  const { tCustomiseWorkTypes } = useKFlags();
  const isWorkTypesEnabled = Boolean(tCustomiseWorkTypes);
  const { data: workTypes = [], isLoading } = useWorkTypes({
    enabled: isWorkTypesEnabled,
  });

  // Depend on the contents, not the array identity — callers pass a literal.
  const ensureKey = ensureIds
    .filter((id) => id !== null && id !== undefined && id !== '')
    .map(String)
    .join('|');

  const options = useMemo<WorkTypeOption[]>(() => {
    if (!isWorkTypesEnabled) return jobsEmploymentsOptions;
    const kept = new Set(ensureKey.split('|').filter(Boolean));
    return workTypes
      .filter(
        (workType) =>
          includeDisabled || workType.enabled || kept.has(String(workType.id))
      )
      .map((workType) => ({
        label: workType.name,
        value: workType.id,
        // Kept only so a saved value renders — not a choice on offer.
        ...(!workType.enabled && !includeDisabled ? { isDisabled: true } : {}),
      }));
  }, [isWorkTypesEnabled, workTypes, includeDisabled, ensureKey]);

  return {
    options,
    isWorkTypesEnabled,
    isLoading: isWorkTypesEnabled && isLoading,
  };
};
